import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ApiService } from '@api/api.service';
import { ModalService } from '@shared/modal/modal.service';
import { HashService } from '@shared/services/hash.service';
import { Subscription } from 'rxjs';

interface AuthenticateFormGroup {
  password: string;
  scope: string;
}

@Component({
  selector: 'rav-extra-authentication',
  templateUrl: './extra-authentication.component.html'
})
export class ExtraAuthenticationComponent implements OnInit, OnDestroy {
  authenticateFormGroup: FormGroup;
  isAuthenticating = false;
  scope: string;
  isOpen = false;
  previousRequest: any;
  type: string;

  private subscriptionList: Subscription = new Subscription();

  constructor(
    private modalService: ModalService,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private router: Router,
    private hashService: HashService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.authenticateFormGroup = this.formBuilder.group({
      password: new FormControl('', Validators.required)
    });

    this.subscriptionList.add(
      this.modalService.open.subscribe((data) => {
        if (!data || data.modalId !== 'authentication') return;
        if (this.isOpen) return;
        this.authenticateFormGroup = this.formBuilder.group({
          password: new FormControl('', Validators.required)
        });
        this.isOpen = true;
        this.isAuthenticating = false;
        this.scope = data.scope;
        this.previousRequest = data.request;
        this.type = data.type;
      })
    );

    this.subscriptionList.add(
      this.modalService.close.subscribe((data) => {
        if (!data || data.modalId !== 'authentication') return;
        if (!this.isOpen) return;
        this.isOpen = false;
        this.router.navigated = false;
        this.router.navigate([this.router.url]);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptionList.unsubscribe();
  }

  async authenticate(values: AuthenticateFormGroup): Promise<void> {
    this.isAuthenticating = true;
    const password_hash = await this.hashService.hash(values.password);
    const requestBody = { password_hash, password: values.password, scope: this.scope };

    this.apiService.additionAuthentication.post(requestBody).subscribe(
      () => {
        this.previousRequest
          .toPromise()
          .then(() => {
            switch (this.type) {
              case '2fa':
                this.displayMessage('Two factor authentication has been disabled', true);
                break;
              case 'profile':
                this.displayMessage('Profile details have been updated successfully', true);
                break;
              case 'account':
                this.displayMessage('Your account details have been updated successfully', true);
                break;
            }
          })
          .catch(() => {
            switch (this.type) {
              case '2fa':
                this.displayMessage('Failed to disable two factor authentication', false);
                break;
              case 'profile':
                this.displayMessage('Failed to update profile details', false);
                break;
              case 'account':
                this.displayMessage('Failed to update account details', false);
                break;
            }
          });

        this.isAuthenticating = false;
        this.modalService.close.next({ modalId: 'authentication' });
      },
      (error) => {
        if (error && error.error) {
          error.error.message = 'Invalid authentication credential';
        }
        this.apiService.onFormError(error, this.authenticateFormGroup);
        this.isAuthenticating = false;
      },
      () => {
        this.isAuthenticating = false;
      }
    );
  }

  /**
   * Display the snackbar message
   *
   * @param message Message to be displayed.
   * @param success Is a success message or failed message
   */
  private displayMessage(message: string, success: boolean): void {
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: success ? ['success-bg', 'bold', 'light-color'] : ['alert-bg', 'bold', 'light-color']
    });
  }
}
